"use client";
import Image from "next/image"
import Reveal from "../../enterprise/components/Reveal"
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useTranslations } from "next-intl";
import Link from "next/link";

export default function BlockChainSection() {
    const t = useTranslations('user');

    const data = [
        {
            title: t('compute-verification-layer'),
            subtitle: t('validates-ai-computations-across-devices-ensuring-your-contributions-are-trusted-and-rewarded-without-technical-hassle'),
        },
        {
            title: t('proof-of-useful-work-consensus'),
            subtitle: t('connects-your-smartphone-or-gpu-to-a-global-network-letting-you-earn-rewards-by-sharing-computing-power-effortlessly'),
        },
        {
            title: t('global-compute-mesh-depin'),
            subtitle: t('connects-your-smartphone-or-gpu-to-a-global-network-letting-you-earn-rewards-by-sharing-computing-power-effortlessly'),
        },
         {
            title: t('dao-governance-and-treasury'),
            subtitle: t('vote-on-hyras-future-and-share-in-rewards-through-transparent-decentralized-decisions'),
        },
    ]

    return(
        <section id="next-section" className="px-20 max-lg:px-10 max-sm:px-4 py-[120px] overflow-hidden">
            <div className="flex flex-col justify-center items-center">
                <Reveal 
                    delay={0.1} 
                    duration={0.8} 
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <h2 className="text-black text-[48px] max-lg:text-[32px]/[40px] font-normal leading-[56px] tracking-[-1px] text-center">{t('a-blockchain-built-for')} <p className="text-[#06F]">{t('ai-development')}</p></h2>
                </Reveal>
                <motion.div
                    initial={{ opacity: 0, translateY: 40 }}
                    whileInView={{ opacity: 1, translateY: 0 }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    viewport={{ once: true }}             
                >
                    <Link href={"/developer"}>
                        <button className="bg-[#0066FF] px-[30px] py-[14px] mt-8 font-medium text-base text-white rounded-[12px]">
                            {t('build-on-hyra')}
                        </button>
                    </Link>
                </motion.div>
            </div>
            <div className="mt-16 max-lg:mt-10 flex justify-between max-w-7xl mx-auto w-full relative">
                {/* mobile */}
                <div className="sm:hidden flex flex-col gap-4">
                    {data.map((item, index) => (
                        <div
                            key={index}
                            className="!h-auto w-full rounded-[32px] bg-white/60 shadow flex flex-col"
                            >
                            <div className="h-full p-6 flex flex-col">
                                <h3 className="text-xl mb-2">{item.title}</h3>
                                <p className="text-[#646464] text-sm flex-1 font-light">{item.subtitle}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* tablet */}
                <div className="w-full min-h-[244px] pb-28 hidden max-lg:block max-sm:hidden">
                    <Swiper
                        spaceBetween={16}
                        slidesPerView={"auto"}
                        freeMode={true}
                        className="!overflow-visible"
                    >
                        {data.map((item, index) => (
                             <SwiperSlide
                                key={index}
                                className="!h-auto max-w-[219px] rounded-[32px] bg-white shadow flex flex-col"
                                >
                                <div className="h-full p-6 flex flex-col">
                                    <h3 className="text-xl mb-2">{item.title}</h3>
                                    <p className="text-[#646464] text-sm flex-1 font-light">{item.subtitle}</p>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {/* desktop */}
                <motion.div
                    initial={{ opacity: 0, translateX: -40 }}
                    whileInView={{ opacity: 1, translateX: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    viewport={{ once: true }}
                    className="max-lg:hidden"      
                >
                    <div className="flex flex-col gap-[100px] items-between">
                        <div>
                            <p className="text-2xl font-normal text-[#202020]">{data[0].title}</p>
                            <p className="mt-6 text-[#646464] text-[16px]">{data[0].subtitle}</p>
                        </div>
                        <div>
                            <p className="text-2xl font-normal text-[#202020]">{data[1].title}</p>
                            <p className="mt-6 text-[#646464] text-[16px]">{data[1].subtitle}</p>
                        </div>
                    </div>
                </motion.div>
                <div className=" relative md:min-w-[500px] h-[450px] max-lg:-z-10 max-lg:absolute max-lg:bottom-0 max-lg:translate-y-[80%] ">
                    {/* <div className="overflow-hidden w-[500px] h-auto translate-y-5"> */}
                        <video
                            className="w-full h-full sm:object-cover opacity-60 scale-[2.75] sm:scale-90 translate-x-4 -translate-y-[60px] rounded-full 
                                [mask-image:radial-gradient(circle_at_center,black_15%,transparent_70%)]
                                [-webkit-mask-image:radial-gradient(circle_at_center,black_15%,transparent_70%)]"
                            autoPlay
                            loop
                            muted
                            playsInline
                            >
                            <source src="https://cdn-r2.hyra.network/hyra-network/radial-pattern.webm" type="video/webm"/>
                        </video>
                    {/* </div> */}
                    <Image 
                        src="/images/backgrounds/bg-section-2-user1.png" 
                        alt="blockchain graphic" 
                        width={1000} 
                        height={600} 
                        className="scale-[2.2] z-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[50%] max-sm:hidden"
                    />
                </div>
                <motion.div
                    initial={{ opacity: 0, translateX: 40 }}
                    whileInView={{ opacity: 1, translateX: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    viewport={{ once: true }}
                    className="max-lg:hidden"              
                >
                    <div className="flex flex-col gap-[100px] items-between">
                        <div>
                            <p className="text-2xl font-normal text-[#202020]">{data[2].title}</p>
                            <p className="mt-6 text-[#646464] text-[16px]">{data[2].subtitle}.</p>
                        </div>
                        <div>
                            <p className="text-2xl font-normal text-[#202020]">{data[3].title}</p>
                            <p className="mt-6 text-[#646464] text-[16px]">{data[3].subtitle}</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    )
};

